import { useEffect, useMemo, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { getAuctionPackages } from "@/lib/api";
import useCountdown, { isRolling } from "@/lib/useCountdown";
import ApplicationForm from "@/components/site/ApplicationForm";
import WaitingListForm from "@/components/site/WaitingListForm";

// Distinct visual palettes per package (colors, gradient, icon, image)
const STYLES = {
  "chicago-2026": {
    grad: "from-[#c8102e] via-[#0075c9] to-[#e8c876]",
    bg: "from-[#ffe6ea] via-[#eef6ff] to-[#fff6dc]",
    accent: "#c8102e", accent2: "#0075c9", ink: "#0a0908",
    icon: "🏙️",
    img: "https://images.unsplash.com/photo-1494522358652-f30e61a60313?w=1200&q=80&auto=format&fit=crop",
    chipBg: "bg-[#c8102e] text-white",
  },
  "dubai-2027": {
    grad: "from-[#0f5d3a] via-[#1a7a4f] to-[#e8c876]",
    bg: "from-[#eafff4] via-[#fff9e5] to-[#ffffff]",
    accent: "#0f5d3a", accent2: "#c9a24b", ink: "#0a0908",
    icon: "🏜️",
    img: "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=1200&q=80&auto=format&fit=crop",
    chipBg: "bg-[#0f5d3a] text-white",
  },
  "la-grammy-2027": {
    grad: "from-[#4b2e83] via-[#ff2ea8] to-[#e8c876]",
    bg: "from-[#f3e6ff] via-[#ffe6f3] to-[#fff6dc]",
    accent: "#7a1fbf", accent2: "#ff2ea8", ink: "#0a0908",
    icon: "🎤",
    img: "https://images.unsplash.com/photo-1519741497674-611481863552?w=1200&q=80&auto=format&fit=crop",
    chipBg: "bg-[#7a1fbf] text-white",
  },
  "la-superbowl-2027": {
    grad: "from-[#0075c9] via-[#003a70] to-[#e8c876]",
    bg: "from-[#eef6ff] via-[#e5efff] to-[#fff6dc]",
    accent: "#0075c9", accent2: "#c9a24b", ink: "#0a0908",
    icon: "🏆",
    img: "https://images.unsplash.com/photo-1508672019048-805c876b67e2?w=1200&q=80&auto=format&fit=crop",
    chipBg: "bg-[#0075c9] text-white",
  },
  "austin-2027": {
    grad: "from-[#ff7a1a] via-[#22c1c3] to-[#fff2d8]",
    bg: "from-[#fff2e0] via-[#e8fbfb] to-[#fff9ec]",
    accent: "#ff7a1a", accent2: "#22c1c3", ink: "#0a0908",
    icon: "🎸",
    img: "https://images.unsplash.com/photo-1531058020387-3be344556be6?w=1200&q=80&auto=format&fit=crop",
    chipBg: "bg-[#ff7a1a] text-white",
  },
  "orlando-2027": {
    grad: "from-[#00c9d1] via-[#ffd400] to-[#ff6f61]",
    bg: "from-[#e6fbfd] via-[#fffde5] to-[#ffe9e5]",
    accent: "#00b1b8", accent2: "#ff6f61", ink: "#0a0908",
    icon: "🌴",
    img: "https://images.unsplash.com/photo-1597466599360-3b9775841aec?w=1200&q=80&auto=format&fit=crop",
    chipBg: "bg-[#00b1b8] text-white",
  },
  "nairobi-2027": {
    grad: "from-[#0f5d3a] via-[#c8102e] to-[#e8c876]",
    bg: "from-[#eafff4] via-[#ffe6ea] to-[#fff6dc]",
    accent: "#0f5d3a", accent2: "#c8102e", ink: "#0a0908",
    icon: "🦁",
    img: "https://images.unsplash.com/photo-1516426122078-c23e76319801?w=1200&q=80&auto=format&fit=crop",
    chipBg: "bg-black text-[#e8c876]",
  },
  "chicago-summer-2027": {
    grad: "from-[#ffd400] via-[#0075c9] to-[#e8c876]",
    bg: "from-[#fffbe0] via-[#eef6ff] to-[#fff6dc]",
    accent: "#0075c9", accent2: "#ffd400", ink: "#0a0908",
    icon: "☀️",
    img: "https://images.unsplash.com/photo-1477884213360-7e9d7dcc1e48?w=1200&q=80&auto=format&fit=crop",
    chipBg: "bg-[#ffd400] text-black",
  },
  "bali-2027": {
    grad: "from-[#0fa675] via-[#00b7d6] to-[#ff70b6]",
    bg: "from-[#e6fbf3] via-[#e5f8fc] to-[#ffe6f2]",
    accent: "#0fa675", accent2: "#ff70b6", ink: "#0a0908",
    icon: "🌺",
    img: "https://images.unsplash.com/photo-1537996194471-e657df975ab4?w=1200&q=80&auto=format&fit=crop",
    chipBg: "bg-[#0fa675] text-white",
  },
  "vegas-superzoo-2027": {
    grad: "from-[#ff2ea8] via-[#7a1fbf] to-[#e8c876]",
    bg: "from-[#ffe6f3] via-[#f3e6ff] to-[#fff6dc]",
    accent: "#ff2ea8", accent2: "#7a1fbf", ink: "#0a0908",
    icon: "🎰",
    img: "https://images.unsplash.com/photo-1605833556294-ea5c7a74f57d?w=1200&q=80&auto=format&fit=crop",
    chipBg: "bg-black text-[#ff2ea8]",
  },
  "miami-basel-2027": {
    grad: "from-[#ff3d9a] via-[#22c1c3] to-[#ff7a1a]",
    bg: "from-[#ffe6f2] via-[#e6fbfc] to-[#fff2e0]",
    accent: "#ff3d9a", accent2: "#22c1c3", ink: "#0a0908",
    icon: "🌴",
    img: "https://images.unsplash.com/photo-1535498730771-e735b998cd64?w=1200&q=80&auto=format&fit=crop",
    chipBg: "bg-[#ff3d9a] text-white",
  },
  "online-private": {
    grad: "from-[#3d6bff] via-[#7a3dff] to-[#ffffff]",
    bg: "from-[#eef1ff] via-[#f3e8ff] to-[#ffffff]",
    accent: "#3d6bff", accent2: "#7a3dff", ink: "#0a0908",
    icon: "💻",
    img: "https://images.unsplash.com/photo-1583337130417-3346a1be7dee?w=1200&q=80&auto=format&fit=crop",
    chipBg: "bg-[#3d6bff] text-white",
  },
  "online-group": {
    grad: "from-[#a4d800] via-[#ff8a1e] to-[#3d6bff]",
    bg: "from-[#f2ffdc] via-[#fff1e0] to-[#eef1ff]",
    accent: "#79a800", accent2: "#ff8a1e", ink: "#0a0908",
    icon: "👥",
    img: "https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=1200&q=80&auto=format&fit=crop",
    chipBg: "bg-[#79a800] text-white",
  },
  "private-inperson": {
    grad: "from-[#c8102e] via-[#e8c876] to-[#ffffff]",
    bg: "from-[#ffe6ea] via-[#fff6dc] to-[#ffffff]",
    accent: "#c8102e", accent2: "#c9a24b", ink: "#0a0908",
    icon: "✦",
    img: "https://images.unsplash.com/photo-1568572933382-74d440642117?w=1200&q=80&auto=format&fit=crop",
    chipBg: "bg-[#c8102e] text-white",
  },
};

const FILTERS = [
  { id: "all", label: "All Experiences" },
  { id: "boot-camp", label: "Boot Camp" },
  { id: "private", label: "Private Training" },
  { id: "group", label: "Group Classes" },
  { id: "online", label: "Online Training" },
  { id: "us", label: "United States" },
  { id: "international", label: "International" },
  { id: "available", label: "Available Now" },
  { id: "coming-soon", label: "Coming Soon" },
];

function UrgencyBadge({ pkg }) {
  if (!pkg.has_capacity) return (
    <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] tracking-[0.22em] uppercase font-semibold bg-[#0f5d3a] text-white">
      Applications open
    </span>
  );
  const r = pkg.remaining_slots, t = pkg.total_slots;
  if (r <= 0) return (
    <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] tracking-[0.22em] uppercase font-semibold bg-black text-[#c8102e]" data-testid={`badge-soldout-${pkg.slug}`}>
      Sold Out — Waitlist Open
    </span>
  );
  if (r <= 3) return (
    <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] tracking-[0.22em] uppercase font-semibold bg-[#c8102e] text-white animate-pulse" data-testid={`badge-few-${pkg.slug}`}>
      Only {r} position{r === 1 ? "" : "s"} left
    </span>
  );
  if (r / t <= 0.5) return (
    <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] tracking-[0.22em] uppercase font-semibold bg-[#c9a24b] text-black" data-testid={`badge-half-${pkg.slug}`}>
      {r} positions remaining
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] tracking-[0.22em] uppercase font-semibold bg-white/95 text-black">
      Applications under review
    </span>
  );
}

function matchesFilter(pkg, f) {
  if (f === "all") return true;
  if (f === "us" || f === "international") return pkg.region === f;
  if (f === "available" || f === "coming-soon") return pkg.status === f;
  return pkg.category === f;
}

function CountdownChip({ target, accent }) {
  const { d, h, m, s, done } = useCountdown(target);
  if (isRolling(target)) return null;
  if (done) return (
    <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] tracking-[0.2em] uppercase font-semibold bg-black text-white">
      <span className="w-1.5 h-1.5 rounded-full bg-red-500"/> Window closed
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] tracking-[0.16em] uppercase font-semibold bg-white/95 text-black shadow-sm" style={{ borderLeft: `3px solid ${accent}` }}>
      <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: accent }}/>
      {d}d · {String(h).padStart(2,"0")}h · {String(m).padStart(2,"0")}m · {String(s).padStart(2,"0")}s
    </span>
  );
}

export default function Packages() {
  const [pkgs, setPkgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [modal, setModal] = useState(null);
  const [waitlist, setWaitlist] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await getAuctionPackages();
      setPkgs(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const filtered = useMemo(() => pkgs.filter((p) => matchesFilter(p, filter)), [pkgs, filter]);
  const active = pkgs.find((p) => p.slug === modal);
  const openModal = (slug) => setModal(slug);

  return (
    <section id="all-packages" className="relative bg-[#f7f1e5]" data-testid="packages-section">
      <div className="max-w-[1600px] mx-auto px-5 md:px-10 py-16 md:py-24">
        {/* Header */}
        <div className="grid md:grid-cols-12 gap-6 items-end mb-8">
          <div className="md:col-span-7">
            <div className="text-[10px] tracking-[0.28em] uppercase text-black/60">Every Training Experience · 2026–2027</div>
            <h2 className="mt-3 font-display text-[clamp(2.2rem,6.5vw,6rem)] leading-[0.92] tracking-[-0.02em] text-[#0a0908]" data-testid="packages-headline">
              Choose your <span className="italic" style={{
                background: "linear-gradient(90deg,#c8102e,#c9a24b,#4b2e83)",
                WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent",
              }}>city.</span>
            </h2>
            <p className="mt-4 max-w-2xl text-black/70 font-editorial text-lg">
              14 curated training experiences with Toriano — from Chicago boot camps to Bali retreats, Grammy Week to online private coaching. Every card is real inventory. No fake scarcity.
            </p>
          </div>
          <div className="md:col-span-5 text-[10px] tracking-[0.24em] uppercase text-black/55 md:text-right">
            Filter · {filtered.length} of {pkgs.length} shown
          </div>
        </div>

        {/* Filter chips */}
        <div className="flex flex-wrap gap-2 mb-10 -mx-1" data-testid="filter-bar">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`text-[11px] tracking-[0.22em] uppercase font-semibold px-4 py-2.5 rounded-full transition-all ${filter === f.id ? "bg-black text-[#e8c876] shadow-lg" : "bg-white/70 text-black/70 hover:bg-white border border-black/10"}`}
              data-testid={`filter-${f.id}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="pkg-grid">
          {loading && <div className="col-span-full text-center py-16 text-black/50">Loading packages…</div>}
          {!loading && filtered.length === 0 && (
            <div className="col-span-full text-center py-16 text-black/50">No packages match this filter.</div>
          )}
          <AnimatePresence mode="popLayout">
            {filtered.map((p, i) => {
              const s = STYLES[p.slug] || STYLES["chicago-2026"];
              const full = p.has_capacity && p.remaining_slots <= 0;
              return (
                <motion.article
                  key={p.slug}
                  layout
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  transition={{ duration: 0.45, delay: 0.03 * (i % 8) }}
                  className="group relative rounded-3xl overflow-hidden shadow-[0_20px_60px_-30px_rgba(0,0,0,0.28)] flex flex-col"
                  style={{ background: `linear-gradient(180deg, var(--tw-gradient-stops))` }}
                  data-testid={`card-${p.slug}`}
                >
                  {/* Colored top rail */}
                  <div className={`h-3 w-full bg-gradient-to-r ${s.grad}`} />

                  {/* Image */}
                  <div className="relative aspect-[16/10] overflow-hidden">
                    <img src={s.img} alt={p.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.06]" />
                    <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.45) 100%)" }}/>
                    <div className="absolute top-3 left-3 flex items-center gap-2">
                      <span className={`text-[10px] tracking-[0.22em] uppercase font-semibold px-2.5 py-1 rounded-full ${s.chipBg}`}>{p.city}</span>
                      {p.highlight && <span className="text-[10px] tracking-[0.22em] uppercase font-semibold px-2.5 py-1 rounded-full bg-white text-black">★ Featured</span>}
                    </div>
                    <div className="absolute top-3 right-3 w-11 h-11 rounded-full bg-white/95 grid place-items-center text-2xl shadow-lg">
                      <span aria-hidden>{s.icon}</span>
                    </div>
                    <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between">
                      <div className="text-white">
                        <div className="text-[9px] tracking-[0.24em] uppercase opacity-80">Window</div>
                        <div className="text-sm font-semibold drop-shadow">{p.window_label}</div>
                      </div>
                      <span className={`text-[10px] tracking-[0.22em] uppercase font-semibold px-2.5 py-1 rounded-full ${p.status === "available" ? "bg-[#0f5d3a] text-white" : "bg-white/95 text-black"}`}>
                        {p.status === "available" ? "Available Now" : "Coming Soon"}
                      </span>
                    </div>
                  </div>

                  {/* Body */}
                  <div className={`p-6 bg-gradient-to-b ${s.bg} flex flex-col flex-grow`}>
                    <h3 className="font-display text-2xl md:text-[1.9rem] leading-tight text-[#0a0908]">{p.title}</h3>
                    {p.subtitle && <p className="mt-1.5 text-sm text-black/70">{p.subtitle}</p>}
                    <div className="mt-3 flex items-center flex-wrap gap-2">
                      <UrgencyBadge pkg={p} />
                      <CountdownChip target={p.window_end} accent={s.accent} />
                    </div>

                    <div className="mt-4 flex items-baseline justify-between gap-3">
                      <div>
                        <div className="text-[9px] tracking-[0.22em] uppercase text-black/50">Price</div>
                        <div className="stat-num text-2xl text-[#0a0908]">{p.price_label || `$${p.opening_bid.toLocaleString()}`}</div>
                      </div>
                      {p.has_capacity ? (
                        <div className="text-right">
                          <div className="text-[9px] tracking-[0.22em] uppercase text-black/50">Positions</div>
                          <div className="stat-num text-2xl text-[#0a0908]" data-testid={`remaining-${p.slug}`}>{p.remaining_slots}/{p.total_slots}</div>
                        </div>
                      ) : (
                        <div className="text-right">
                          <div className="text-[9px] tracking-[0.22em] uppercase text-black/50">Availability</div>
                          <div className="text-sm font-semibold" style={{ color: s.accent }}>Applications open</div>
                        </div>
                      )}
                    </div>

                    {/* Progress bar for capacity ones */}
                    {p.has_capacity && (
                      <div className="mt-3 h-1.5 w-full rounded-full bg-black/10 overflow-hidden">
                        <div className={`h-full bg-gradient-to-r ${s.grad}`} style={{ width: `${Math.max(4, Math.round((p.remaining_slots / p.total_slots) * 100))}%` }}/>
                      </div>
                    )}

                    {/* Perks */}
                    {p.perks && p.perks.length > 0 && (
                      <ul className="mt-4 space-y-1.5">
                        {p.perks.slice(0, 4).map((perk) => (
                          <li key={perk} className="text-xs text-black/75 flex items-start gap-2">
                            <span className="mt-1 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: s.accent }}/>{perk}
                          </li>
                        ))}
                        {p.perks.length > 4 && <li className="text-[10px] tracking-[0.2em] uppercase text-black/45">+ {p.perks.length - 4} more perks</li>}
                      </ul>
                    )}

                    {p.disclaimer && (
                      <div className="mt-3 text-[11px] italic text-[#7a1220]">{p.disclaimer}</div>
                    )}

                    <button
                      onClick={() => full ? setWaitlist(p.slug) : openModal(p.slug)}
                      className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full py-3.5 text-[11px] md:text-[12px] tracking-[0.22em] uppercase font-semibold transition-all mt-auto"
                      style={{
                        background: full ? "#0a0908" : `linear-gradient(90deg, ${s.accent}, ${s.accent2})`,
                        color: "#ffffff",
                        boxShadow: full ? "none" : `0 15px 35px -15px ${s.accent}80`,
                      }}
                      data-testid={`apply-${p.slug}`}
                    >
                      {full ? "Join the Waiting List" : (p.cta_label || "Apply Now")}
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
                    </button>
                  </div>
                </motion.article>
              );
            })}
          </AnimatePresence>
        </div>
      </div>

      {/* Application form */}
      <ApplicationForm open={!!modal} onClose={() => { setModal(null); load(); }} pkg={active} source="website" />
      <WaitingListForm open={!!waitlist} onClose={() => setWaitlist(null)} pkg={pkgs.find((p) => p.slug === waitlist)} />
    </section>
  );
}
